// The team photo (PLAN-2026-10-24-faces-round-three §4): every team's
// character in two rows, as a team photo stands, on the League page. Client
// only: the faces are already in the standings' read.
//
// IN THE STANDINGS' ORDER, from the middle out: the front row the top half (or one more),
// the leader in the middle of it, and the champion there once there is one,
// cheering. A tap on anybody opens their team.
//
// A PICTURE TO KEEP (PLAN-2026-10-31-a-handshake-a-dance-and-the-photo §3):
// Save the photo, and Share where the device shares files, draw it on the
// device from the characters on the screen when asked, not when the page
// opens. Nothing is sent and nothing is kept.

import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { StandingsRow, TeamRef } from '@fsp/domain';
import { TeamFigure } from '../../kit/TeamFigure';
import { Button, Line, tell } from '../../kit/ui';
import {
  canShareFiles,
  downloadCanvas,
  drawTeamPhoto,
  shareCard,
  type PhotoPerson,
} from '../../share/shareCard';

/** A row's teams from the middle out: the first in the middle, then left, right, left. */
function middleOut<T>(list: readonly T[]): T[] {
  const out: T[] = [];
  list.forEach((t, i) => {
    if (i % 2 === 1) out.unshift(t);
    else out.push(t);
  });
  return out;
}

export function TeamPhoto({
  rows,
  championId,
  base,
  league,
  kicker,
}: {
  rows: readonly StandingsRow[];
  /** The season's champion, once there is one: front and center. */
  championId: string | null;
  base: string;
  /** The league's name, over the picture. */
  league: string;
  /** The week and the season over the name: "Week 8 · 2026". */
  kicker: string;
}) {
  const photo = useRef<HTMLDivElement | null>(null);
  const [busy, setBusy] = useState(false);
  const teams: TeamRef[] = rows.map((r) => r.team);
  const champion = teams.find((t) => t.id === championId);
  const ordered = champion ? [champion, ...teams.filter((t) => t !== champion)] : teams;
  // THE FRONT ROW HAS A MIDDLE: an odd number in it, one more than half
  // where half is even, so the leader or the champion stands in the middle.
  const half = Math.ceil(ordered.length / 2);
  const inFront = Math.min(ordered.length, half % 2 === 1 ? half : half + 1);
  const front = middleOut(ordered.slice(0, inFront));
  const back = middleOut(ordered.slice(inFront));
  const perRow = Math.max(front.length, back.length);
  const person = (t: TeamRef) => (
    <Link
      key={t.id}
      to={`${base}/teams/${t.id}`}
      data-team={t.id}
      className="fsp-photo-person"
      aria-label={t.id === championId ? `${t.name}, champion` : t.name}
      title={t.name}
    >
      <TeamFigure team={t} mood={t.id === championId ? 'pleased' : 'idle'} />
    </Link>
  );
  // EACH PERSON AS THE SCREEN DRAWS THEM: the character's own drawing, so the
  // picture wears this week's hats and the champion's cheer.
  const said = (t: TeamRef): PhotoPerson => {
    const at = photo.current?.querySelector(`[data-team="${CSS.escape(t.id)}"]`) ?? null;
    return {
      name: t.id === championId ? `${t.name} (champion)` : t.name,
      team: t,
      drawing: at?.querySelector<SVGSVGElement>('svg.fsp-figure') ?? null,
      hat:
        at?.querySelector<SVGSVGElement>('.fsp-teamfigure-placard .fsp-teammark-hat svg') ?? null,
      bot: at?.querySelector<SVGSVGElement>('.fsp-teamfigure-bot svg') ?? null,
    };
  };
  const title = `${league} team photo`;
  const keep = async (how: 'save' | 'share') => {
    if (busy) return;
    setBusy(true);
    try {
      const canvas = await drawTeamPhoto({
        kicker,
        title: league,
        back: back.map(said),
        front: front.map(said),
      });
      if (how === 'save') downloadCanvas(canvas, title);
      else if (!(await shareCard(canvas, `${league}, ${kicker}.`, title)))
        tell('The photo was not shared. Save the photo works too.');
    } catch {
      tell('The photo could not be drawn here.', 'bad');
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      {/* HOW MANY TO A ROW is state the stylesheet reads, which sizes each person. */}
      <div
        ref={photo}
        className="fsp-photo"
        role="group"
        aria-label="Team photo"
        data-per-row={perRow}
      >
        {back.length > 0 ? (
          <div className="fsp-photo-row fsp-photo-back">{back.map(person)}</div>
        ) : null}
        <div className="fsp-photo-row fsp-photo-front">{front.map(person)}</div>
      </div>
      <Line gap={2} wrap>
        <Button size="sm" icon="download" disabled={busy} onClick={() => void keep('save')}>
          Save the photo
        </Button>
        {canShareFiles() ? (
          <Button size="sm" icon="share" disabled={busy} onClick={() => void keep('share')}>
            Share
          </Button>
        ) : null}
      </Line>
    </>
  );
}
